import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import Cookies from "js-cookie";
import { notification, Spin, Card, Button, Input, Modal, Divider, Empty, Tag, Tooltip, Table } from "antd";
import moment from "moment";
import {
  HiOutlineChevronLeft,
  HiOutlineChatAlt2,
  HiOutlineDocumentText,
  HiOutlinePlus,
  HiOutlineEye,
  HiOutlinePaperClip,
  HiOutlineSearch,
  HiOutlineCalendar,
  HiOutlineClock,
  HiOutlineSparkles,
  HiOutlineSave,
  HiOutlineDatabase,
  HiOutlineThumbUp,
  HiOutlineRefresh
} from 'react-icons/hi';
import { encryptData, decryptData } from '../Crypto_Helpers/cryptoHelper';
import { initEncryptionSession } from '../../encryptionSession';

// Icons
import commenticon from "../../assets/clinicIcon/Group 189.svg";
import prescriptionicon from "../../assets/clinicIcon/Group 17352.svg";
import reporticon from "../../assets/clinicIcon/Group 17353.svg";
import MaleIcon from "../../assets/Doctor_Images/male_user.png";
import FemaleIcon from "../../assets/Doctor_Images/female_user.png";

const { TextArea } = Input;

const Report = () => {
  const history = useHistory();
  const { patientId, bookingId, date } = useParams();

  // State
  const [loading, setLoading] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [comments, setComments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [clinicReports, setClinicReports] = useState([]);
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [clinicProfile, setClinicProfile] = useState(null);

  // Selection state for side preview (for documents/files)
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'pdf', 'image', 'prescription'

  // Modal states
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState(false);
  const [isViewCommentModalOpen, setIsViewCommentModalOpen] = useState(false);
  const [activeComment, setActiveComment] = useState("");
  const [newComment, setNewComment] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  
  // AI Assistant State
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiSymptoms, setAiSymptoms] = useState("");
  const [aiHistory, setAiHistory] = useState([]);
  const [isAiHistoryOpen, setIsAiHistoryOpen] = useState(false);

  const isPDF = (url) => {
    if (!url) return false;
    // Check if it contains .pdf before the query params or at the end
    const pathOnly = url.split('?')[0].toLowerCase();
    return pathOnly.endsWith('.pdf');
  };

  useEffect(() => {
    fetchAllData();
  }, [patientId, bookingId, date]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAppointmentDetails(),
        fetchComments(),
        fetchPrescriptions(),
        fetchClinicReports(),
        fetchAiHistory(),
        fetchClinicDetails()
      ]);
    } catch (err) {
      console.error("Failed to fetch page data", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAppointmentDetails = async () => {
    try {
      // Using doctorweb endpoint and capitalized cookie keys for compatibility
      const response = await axiosInstance.post("doctor/getAppointmentList", {
        clinic_id: Cookies.get("Clinic_id") || Cookies.get("clinic_id"),
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patient_type: "",
        period: "day",
        search_date: date,
        search_date_to: date
      });

      if (response.data.data && response.data.data[0]) {
        // Result is already decrypted by axiosInstance interceptor case 3
        const decryptedData = response.data.data[0].result;
        if (Array.isArray(decryptedData)) {
          const filtered = decryptedData.find(x => x.patient_id == patientId && x.booking_id == bookingId);
          if (filtered) setAppointment(filtered);
        }
      }
    } catch (err) {
      console.error("Appointment fetch error", err);
    }
  };

  const fetchComments = async () => {
    try {
      const response = await axiosInstance.post("doctor/getReportComment", {
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id") || appointment?.doctor_id,
        patient_id: patientId
      });
      let commentsData = response.data.data || response.data || [];
      if (typeof commentsData === 'string') {
        const aesKey = await initEncryptionSession();
        commentsData = await decryptData(commentsData, aesKey);
      }
      setComments(Array.isArray(commentsData) ? commentsData : []);
    } catch (err) {
      console.error("Comments fetch error", err);
    }
  };

  const fetchPrescriptions = async () => {
    try {
      const response = await axiosInstance.post("doctor/getReportPrescriptionImage", {
        doctorId: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patientId: patientId
      });
      setPrescriptions(response.data.data || response.data || []);
    } catch (err) {
      console.error("Prescriptions fetch error", err);
    }
  };

  const fetchClinicDetails = async () => {
    try {
      const response = await axiosInstance.post("doctor/getClinicProfile", {
        clinic_id: Cookies.get("Clinic_id") || Cookies.get("clinic_id")
      });
      setClinicProfile(response.data.data && response.data.data[0] ? response.data.data[0] : null);
    } catch (err) {
      console.error("Clinic fetch error", err);
    }
  };

  const fetchClinicReports = async () => {
    try {
      const response = await axiosInstance.post("doctor/getReportsByClinic", {
        patientId: patientId,
        doctorId: Cookies.get("Doctor_id") || Cookies.get("doctor_id") || appointment?.doctor_id,
        clinicId: Cookies.get("Clinic_id") || Cookies.get("clinic_id") || appointment?.clinic_id,
        frmreq: "clinic",
        dateBasedSorting: true,
        dateBasedSortingOrder: "DESC",
        limit: 100,
        pageno: 1,
      });

      const detailsData = response.data.data && response.data.data[0] ? response.data.data[0].details : null;
      let decrypted = detailsData;
      if (typeof detailsData === 'string') {
        const aesKey = await initEncryptionSession();
        decrypted = await decryptData(detailsData, aesKey);
      }
      setClinicReports(Array.isArray(decrypted) ? decrypted : []);
    } catch (err) {
      console.error("Clinic reports fetch error", err);
    }
  };

  const fetchAiHistory = async () => {
    try {
      const response = await axiosInstance.post("ai/get-patient-analysis-history", {
        patient_id: patientId
      });
      setAiHistory(response.data.data || []);
    } catch (err) {
      console.error("AI history fetch error", err);
    }
  };

  const handleAiAnalysis = async () => {
    if (!aiSymptoms.trim()) {
      notification.warning({ message: "Please enter patient symptoms for analysis" });
      return;
    }
    setAiLoading(true);
    try {
      const response = await axiosInstance.post("ai/doctor-prediction", {
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patient_id: patientId,
        symptoms: aiSymptoms,
        service_type: appointment?.service_name || "General Checkup",
        pre_exits: JSON.stringify(comments.map(c => c.comments)),
        metrics: {
            age: appointment?.age,
            gender: appointment?.gender
        }
      });
      // response.data already handled by interceptor
      setAiResult(response.data.data || response.data);
    } catch (err) {
      notification.error({ message: "AI Assistant is busy", description: err.message });
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveAiAnalysis = async () => {
    if (!aiResult) return;
    try {
        await axiosInstance.post("ai/save-analysis", {
            doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
            patient_id: patientId,
            appointment_id: bookingId,
            symptoms: aiSymptoms,
            diagnosis: aiResult.ai_prediction,
            reasoning: aiResult.reasoning,
            medicines: aiResult.generic_suggestions
        });
        notification.success({ message: "AI Insight saved to Clinical Archive and Doctor's Notes" });
        fetchAiHistory();
        fetchComments(); // Refresh the standard notes list
    } catch (err) {
        notification.error({ message: "Failed to persist AI analysis" });
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      notification.warning({ message: "Please enter a comment" });
      return;
    }
    setIsSubmittingComment(true);
    try {
      const response = await axiosInstance.post("doctor/addReportComment", {
        doctor_id: Cookies.get("Doctor_id") || Cookies.get("doctor_id") || appointment?.doctor_id,
        patient_id: patientId,
        comments: newComment
      });
      if (response.data.status === 1 || response.data.status === 200) {
        notification.success({ message: "Comment added successfully" });
        setNewComment("");
        setIsAddCommentModalOpen(false);
        fetchComments();
      } else {
        notification.error({ message: response.data.msg || "Failed to add comment" });
      }
    } catch (err) {
      notification.error({ message: "Failed to add comment" });
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentView = (comment) => {
    setActiveComment(comment);
    setIsViewCommentModalOpen(true);
  };

  const fetchExistingPrescription = async (targetBookingId) => {
    try {
      const response = await axiosInstance.post("doctor/getPrescriptionImage", {
        doctorId: Cookies.get("Doctor_id") || Cookies.get("doctor_id"),
        patientId: patientId,
        booking_id: targetBookingId,
      });

      const records = response.data.data || response.data;
      if (Array.isArray(records)) {
        const record = records[0];
        if (record && record.p_medicinedetails) {
          try {
            const parsed = typeof record.p_medicinedetails === 'string'
              ? JSON.parse(record.p_medicinedetails)
              : record.p_medicinedetails;
            setSelectedMedications(Array.isArray(parsed) ? parsed : []);
            // Update selectedPreview with the full record from the specialized API
            setSelectedPreview(record);
          } catch (e) {
            console.error("Prescription Parse Error", e);
            setSelectedMedications([]);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch existing prescription", err);
    }
  };

  const handleFilePreview = async (item, type) => {
    setSelectedPreview(item);
    setPreviewType(type);
    setSelectedMedications([]); // Reset medications for new preview

    if (type === 'prescription') {
      await fetchExistingPrescription(item.doctor_booking_id || item.id || bookingId);
    }
  };

  if (loading && !appointment) {
    return <div className="h-screen w-full flex items-center justify-center bg-teal-50"><Spin size="large" /></div>;
  }

  return (
    <div className="report_page_container animate-in fade-in duration-700 min-h-screen bg-slate-50/50">
      {/* Header Section */}
      <div className="bg-white/90 backdrop-blur-md border-b border-slate-100 sticky top-0 z-30 px-8 py-4 shadow-sm">
        <div className="flex items-center justify-between gap-6 max-w-[1600px] mx-auto">
          <div className="flex items-center gap-4">
            <button onClick={() => history.goBack()} className="p-2.5 hover:bg-teal-50 rounded-2xl text-teal-600 transition-all active:scale-95">
              <HiOutlineChevronLeft size={24} />
            </button>
            <div>
              <h2 className="text-2xl font-black text-slate-800 tracking-tight">Clinical Archive</h2>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-0.5">Patient History & Diagnostics</p>
            </div>
          </div>

          <button onClick={() => history.push(`/dashboard/appointments/details/${bookingId}`)} className="ml-auto mr-4 flex items-center gap-2 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold rounded-2xl transition-all shadow-md active:scale-95">
            <HiOutlineThumbUp size={18} />
            <span>Done</span>
          </button>
          {appointment && (
            <div className="flex items-center gap-6 px-6 py-2.5 bg-teal-50 rounded-[28px] border border-teal-100/50">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl overflow-hidden border-2 border-white shadow-md">
                  <img src={appointment.gender?.toLowerCase().startsWith('m') ? MaleIcon : FemaleIcon} alt="patient" />
                </div>
                <div>
                  <p className="text-sm font-black text-slate-800 leading-none">{appointment.name}</p>
                  <p className="text-[10px] text-teal-600 font-black uppercase tracking-wider mt-1.5">ID: ID000{appointment.patient_id}</p>
                </div>
              </div>
              <Divider type="vertical" className="h-8 border-teal-200/50" />
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none">Age</p>
                  <p className="text-xs font-black text-slate-700 mt-1">{appointment.age} Yrs</p>
                </div>
                <div className="text-center">
                  <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest leading-none">Gender</p>
                  <p className="text-xs font-black text-slate-700 mt-1">{appointment.gender}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Split View */}
      <div className="max-w-[1600px] mx-auto p-8 flex gap-8 h-[calc(100vh-100px)]">
        {/* Left Side: Interactive Lists */}
        <div className="w-[35%] flex flex-col gap-10 overflow-y-auto pr-3 pt-2 custom-scrollbar">
          {/* 0. AI Clinical Intelligence Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-slate-800 flex items-center justify-center">
                  <HiOutlineSparkles className="text-teal-400" size={20} />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm">AI Clinical Intelligence</h3>
                  <p className="text-[10px] text-teal-600 font-black uppercase tracking-widest">Active Analysis</p>
                </div>
              </div>
              <Tooltip title="View Analysis History">
                <Button 
                    icon={<HiOutlineDatabase />} 
                    type="link" 
                    className="text-slate-400 hover:text-teal-600 p-0 flex items-center"
                    onClick={() => setIsAiHistoryOpen(true)}
                >
                    <span className="ml-1 text-[10px] font-black uppercase tracking-widest">{aiHistory.length} Results</span>
                </Button>
              </Tooltip>
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-[32px] p-6 shadow-xl relative overflow-hidden border border-slate-700">
              <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 rounded-full -mr-10 -mt-10 blur-2xl"></div>
              
              <div className="space-y-4 relative">
                <div className="bg-slate-700/30 rounded-2xl p-4 border border-slate-700">
                  <label className="block text-[9px] text-teal-400 font-bold uppercase tracking-widest mb-2 ml-1">Current Complaints</label>
                  <TextArea
                    rows={3}
                    placeholder="Enter patient symptoms here for AI diagnostics..."
                    variant="borderless"
                    className="p-0 text-white placeholder:text-slate-500 text-sm focus:ring-0 w-full resize-none"
                    style={{ background: 'transparent' }}
                    value={aiSymptoms}
                    onChange={(e) => setAiSymptoms(e.target.value)}
                  />
                </div>

                <Button
                  block
                  loading={aiLoading}
                  onClick={handleAiAnalysis}
                  className="h-12 bg-teal-500 hover:!bg-teal-400 border-none rounded-2xl font-black text-slate-900 uppercase tracking-widest text-xs shadow-lg shadow-teal-500/20"
                >
                  {aiLoading ? "Consulting AI..." : "Run AI Diagnostics"}
                </Button>

                {aiResult && (
                  <div className="mt-6 space-y-4 animate-in slide-in-from-top-4 duration-500">
                    <Divider className="border-slate-700 my-4" />
                    
                    <div className="bg-teal-500/5 rounded-2xl p-4 border border-teal-500/20">
                      <p className="text-teal-400 text-[10px] font-black uppercase tracking-widest mb-1">Predicted Diagnosis</p>
                      <h4 className="text-white font-black text-base leading-tight">{aiResult.ai_prediction}</h4>
                    </div>

                    <div className="space-y-2">
                      <p className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-1">Clinical Reasoning</p>
                      <p className="text-slate-300 text-xs leading-relaxed italic">"{aiResult.reasoning}"</p>
                    </div>

                    <div className="flex gap-2">
                        <Button
                          icon={<HiOutlineSave />}
                          onClick={handleSaveAiAnalysis}
                          className="h-10 bg-white/5 hover:!bg-white/10 text-white border-white/20 rounded-xl font-bold text-[10px] uppercase tracking-widest mt-2 flex-1"
                        >
                          Save to Archive
                        </Button>
                        <Button
                          type="primary"
                          icon={<HiOutlineSparkles />}
                          onClick={() => history.push(`/dashboard/appointments/prescription/${patientId}/${bookingId}/${date}/1/clinical-archive`)}
                          className="h-10 bg-emerald-600 hover:!bg-emerald-700 text-white border-none rounded-xl font-bold text-[10px] uppercase tracking-widest mt-2 flex-1"
                        >
                          E-Prescription
                        </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* 1. Clinical Comments Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-teal-100 flex items-center justify-center">
                  <img src={commenticon} alt="comment" className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm">Doctor's Notes</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">{comments.length} Entries</p>
                </div>
              </div>
              <Button
                icon={<HiOutlinePlus />}
                type="primary"
                className="bg-teal-600 rounded-xl hover:!bg-teal-700 border-none font-bold shadow-lg shadow-teal-100"
                onClick={() => setIsAddCommentModalOpen(true)}
              >
                Add Note
              </Button>
            </div>

            <div className="space-y-3">
              {comments.length > 0 ? comments.map((c, i) => (
                <div
                  key={i}
                  className="group bg-white p-5 rounded-[28px] transition-all border border-transparent shadow-sm hover:shadow-md hover:border-teal-100 cursor-pointer flex gap-4"
                  onClick={() => handleCommentView(c.comments)}
                >
                  <div className="w-12 h-12 shrink-0 rounded-2xl bg-teal-50 flex items-center justify-center transition-all group-hover:scale-110">
                    <img src={commenticon} alt="icon" className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] text-teal-600 font-black uppercase tracking-widest">{moment(c.created_on).format("DD MMM YYYY")}</span>
                      <HiOutlineEye size={16} className="text-slate-300 group-hover:text-teal-500 transition-colors" />
                    </div>
                    <p className="text-slate-600 text-sm font-medium line-clamp-2 leading-relaxed">{c.comments}</p>
                  </div>
                </div>
              )) : (
                <div className="bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-[32px] p-8 text-center">
                  <p className="text-slate-400 font-bold italic">No comments added yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* 2. Medical Prescriptions Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 px-2">
              <div className="w-10 h-10 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <img src={prescriptionicon} alt="prescription" className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm">Prescriptions</h3>
                <p className="text-[10px] text-slate-400 font-bold uppercase">{prescriptions.length} Records</p>
              </div>
            </div>

            <div className="grid gap-3">
              {prescriptions.length > 0 ? prescriptions.map((p, i) => (
                <div
                  key={i}
                  onClick={() => handleFilePreview(p, 'prescription')}
                  className={`group p-5 rounded-[28px] transition-all border cursor-pointer flex items-center justify-between ${selectedPreview === p ? 'bg-emerald-50 border-emerald-200 shadow-md translate-x-1' : 'bg-white border-transparent shadow-sm hover:border-emerald-100'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center transition-all group-hover:rotate-6">
                      <img src={prescriptionicon} alt="icon" className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-slate-800 text-sm font-black">
                        PHARM-ORD #{p.id}
                        <span className="ml-2 text-[9px] text-teal-600 opacity-60">BK: #{p.doctor_booking_id || p.booking_id}</span>
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <HiOutlineCalendar size={12} className="text-emerald-400" />
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{moment(p.prescript_date).format("DD MMM YYYY")}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${selectedPreview === p ? 'bg-emerald-500 text-white shadow-lg' : 'bg-slate-50 text-slate-300'}`}>
                    <HiOutlineEye size={16} />
                  </div>
                </div>
              )) : (
                <div className="bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-[32px] p-8 text-center">
                  <p className="text-slate-400 font-bold italic">No prescriptions found.</p>
                </div>
              )}
            </div>
          </div>

          {/* 3. Clinic Lab Reports Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-blue-100 flex items-center justify-center">
                  <img src={reporticon} alt="report" className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800 uppercase tracking-wider text-sm">Clinic Lab Reports</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">{clinicReports.length} Files</p>
                </div>
              </div>
              <Button
                icon={<HiOutlinePlus />}
                type="link"
                className="text-blue-600 font-black uppercase tracking-widest text-[10px]"
                onClick={() => history.push(`/dashboard/appointments/uploadreport/${patientId}/${bookingId}/${date}/touploadreport`)}
              >
                Upload New
              </Button>
            </div>

            <div className="grid gap-3">
              {clinicReports.length > 0 ? clinicReports.map((r, i) => (
                <div
                  key={i}
                  onClick={() => handleFilePreview(r, 'report')}
                  className={`group p-5 rounded-[28px] transition-all border cursor-pointer flex items-center justify-between ${selectedPreview === r ? 'bg-blue-50 border-blue-200 shadow-md translate-x-1' : 'bg-white border-transparent shadow-sm hover:border-blue-100'
                    }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center transition-all group-hover:bg-blue-100">
                      <img src={reporticon} alt="icon" className="w-6 h-6" />
                    </div>
                    <div className="max-w-[180px]">
                      <p className="text-slate-800 text-sm font-black truncate">{r.report_name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <HiOutlineClock size={12} className="text-blue-400" />
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{moment(r.uploaded_date).format("DD MMM YYYY")}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1.5 rounded-xl font-bold text-[9px] uppercase tracking-tighter transition-all ${isPDF(r.report_filepath || r.report_file) ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'
                    }`}>
                    {isPDF(r.report_filepath || r.report_file) ? 'PDF Document' : 'Image View'}
                  </div>
                </div>
              )) : (
                <div className="bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-[32px] p-8 text-center text-slate-400">
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="font-bold italic">No lab reports available.</span>} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side: Smart Preview Viewer */}
        <div className="flex-1 bg-white rounded-[40px] shadow-2xl border border-white overflow-hidden flex flex-col relative transition-all">
          {!selectedPreview ? (
            <div className="flex-1 flex flex-col items-center justify-center p-20 text-center animate-pulse">
              <div className="w-40 h-40 bg-slate-50 rounded-full flex items-center justify-center mb-10 border-4 border-white shadow-inner">
                <HiOutlineEye size={64} className="text-slate-200" />
              </div>
              <h3 className="text-2xl font-black text-slate-800 mb-4">Diagnostic Viewer</h3>
              <p className="text-slate-400 max-w-sm leading-relaxed font-medium">Select a prescription or lab report from the sidebar to inspect documents and files in high-resolution here.</p>
              <div className="mt-8 flex gap-3">
                <div className="w-2 h-2 rounded-full bg-teal-200"></div>
                <div className="w-2 h-2 rounded-full bg-emerald-200"></div>
                <div className="w-2 h-2 rounded-full bg-blue-200"></div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col h-full animate-in slide-in-from-right-10 duration-700">
              {/* Viewer Header */}
              <div className="px-12 py-8 border-b border-slate-50 flex items-center justify-between shrink-0 bg-slate-50/20">
                <div className="flex items-center gap-5">
                  <div className={`w-16 h-16 rounded-[24px] flex items-center justify-center shadow-lg ${previewType === 'prescription' ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' : 'bg-gradient-to-br from-blue-400 to-blue-600'
                    }`}>
                    <img src={previewType === 'prescription' ? prescriptionicon : reporticon} alt="icon" className="w-9 h-9 brightness-0 invert" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-slate-800 leading-none">
                      {previewType === 'prescription'
                        ? `Prescription - Booking #${selectedPreview.doctor_booking_id || selectedPreview.booking_id || bookingId}`
                        : selectedPreview.report_name}
                    </h3>
                    <div className="flex items-center gap-4 mt-2">
                      <Tag className="rounded-full bg-white border-slate-100 text-slate-500 font-bold px-3">
                        {previewType === 'prescription' ? 'Pharmacy Order' : 'Laboratory File'}
                      </Tag>
                      <span className="text-xs font-black text-slate-300 uppercase tracking-widest">
                        {moment(selectedPreview.uploaded_date || selectedPreview.prescript_date).format("MMMM DD, YYYY")}
                      </span>
                    </div>
                  </div>
                </div>
                <Button
                  shape="circle"
                  size="large"
                  icon={<HiOutlineChevronLeft className="rotate-180" />}
                  onClick={() => setSelectedPreview(null)}
                  className="border-slate-100 hover:text-teal-600 shadow-sm"
                />
              </div>

              {/* Viewer Stage */}
              <div className="flex-1 overflow-hidden p-8 bg-slate-900/5 relative">
                {previewType === 'prescription' ? (
                  <div className="h-full flex flex-col bg-white rounded-[32px] shadow-sm border border-slate-100 overflow-hidden">
                    <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                          <img src={prescriptionicon} alt="icon" className="w-6 h-6" />
                        </div>
                        <h4 className="text-lg font-black text-slate-800">
                          Medication Details
                        </h4>
                      </div>
                      <Tag color="emerald" className="rounded-full font-black border-none px-4">Digitized Record</Tag>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                      <Table
                        pagination={false}
                        className="premium-table-report"
                        dataSource={selectedMedications}
                        columns={[
                          {
                            title: 'Medicine',
                            dataIndex: 'name',
                            key: 'name',
                            render: (text) => <span className="font-bold text-slate-700">{text}</span>
                          },
                          {
                            title: 'Dose',
                            key: 'dosage',
                            render: (_, record) => (
                              <div className="flex gap-1">
                                {record.counts.map((c, i) => (
                                  <div key={i} className={`w-5 h-5 rounded flex items-center justify-center text-[9px] font-black ${c > 0 ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-300'}`}>
                                    {i === 0 ? 'M' : i === 1 ? 'A' : i === 2 ? 'E' : 'N'}
                                  </div>
                                ))}
                              </div>
                            )
                          },
                          {
                            title: 'Duration',
                            key: 'duration',
                            render: (_, record) => <span className="text-slate-500 font-bold text-xs">{record.days} Days</span>
                          },
                          {
                            title: 'Instruction',
                            key: 'food',
                            render: (_, record) => (
                              <Tag color={record.foodtype === 0 ? 'orange' : 'cyan'} className="rounded-full border-none px-2 font-bold text-[10px]">
                                {record.foodtype === 0 ? 'Before Food' : 'After Food'}
                              </Tag>
                            )
                          },
                          {
                            title: 'Qty',
                            key: 'qty',
                            align: 'center',
                            render: (_, record) => <span className="font-black text-emerald-700">{record.totalmedicine}</span>
                          }
                        ]}
                      />
                    </div>

                    <div className="p-6 bg-slate-50/30 border-t border-slate-50 flex justify-between items-center px-10">
                      <div className="flex gap-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest self-center mr-4">Digital ID: {selectedPreview.id}</span>
                        <Button
                          icon={<HiOutlineDocumentText />}
                          onClick={() => history.push(`/dashboard/appointments/prescription/${patientId}/${bookingId}/${moment(selectedPreview.prescript_date).format('YYYY-MM-DD')}/1/reissue`)}
                          className="bg-teal-50 text-teal-700 border-teal-100 rounded-xl h-10 px-4 font-black text-[10px] uppercase"
                        >
                          Select for E-Prescription
                        </Button>
                      </div>
                      <Button
                        type="primary"
                        className="bg-slate-800 rounded-xl h-12 px-8 font-black border-none hover:!bg-slate-900"
                        onClick={() => window.print()}
                      >
                        Print Prescription
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="h-full rounded-[32px] overflow-hidden shadow-2xl border-4 border-white bg-white">
                    {isPDF(selectedPreview.report_filepath || selectedPreview.report_file) ? (
                      <object
                        data={(selectedPreview.report_filepath || selectedPreview.report_file) + "#toolbar=0&navpanes=0&scrollbar=0"}
                        type="application/pdf"
                        className="w-full h-full rounded-2xl"
                      >
                        <div className="p-24 text-center">
                          <HiOutlinePaperClip size={48} className="mx-auto mb-4 text-slate-200" />
                          <p className="font-bold text-slate-400">PDF Preview Unavailable</p>
                          <a href={selectedPreview.report_filepath || selectedPreview.report_file} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block text-blue-600 font-bold border-b-2 border-blue-100">Download Document</a>
                        </div>
                      </object>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-50">
                        <img
                          src={selectedPreview.report_filepath || selectedPreview.report_file}
                          alt="report"
                          className="max-w-full max-h-full object-contain shadow-sm"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal: View Comment (Popup) */}
      <Modal
        title={<div className="flex items-center gap-3 py-2">
          <img src={commenticon} alt="icon" className="w-6 h-6" />
          <span className="font-black text-slate-800 tracking-tight">Clinical Note Detail</span>
        </div>}
        open={isViewCommentModalOpen}
        onCancel={() => setIsViewCommentModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewCommentModalOpen(false)} className="rounded-xl font-bold h-10 px-8">Close</Button>,
          <Button key="copy" type="primary" onClick={() => {
            navigator.clipboard.writeText(activeComment);
            notification.success({ message: "Comment copied to clipboard" });
          }} className="bg-teal-600 rounded-xl font-bold h-10 px-8">Copy Content</Button>
        ]}
        className="rounded-3xl"
        centered
        width={600}
      >
        <div className="py-8 px-2">
          <div className="bg-slate-50 p-8 rounded-[32px] border border-slate-100 min-h-[200px] shadow-inner">
            <p className="text-slate-700 text-lg leading-relaxed whitespace-pre-wrap font-medium italic">"{activeComment}"</p>
          </div>
        </div>
      </Modal>

      {/* Modal: Add Comment */}
      <Modal
        title={<div className="font-black text-slate-800 py-2">Create New Clinical Note</div>}
        open={isAddCommentModalOpen}
        onCancel={() => setIsAddCommentModalOpen(false)}
        onOk={handleAddComment}
        okText="Save Observation"
        confirmLoading={isSubmittingComment}
        className="rounded-[32px]"
        okButtonProps={{ className: "bg-teal-600 rounded-xl h-11 px-10 font-bold border-none" }}
        cancelButtonProps={{ className: "rounded-xl h-11 px-6 font-bold" }}
        centered
      >
        <div className="py-6">
          <label className="block text-[10px] text-slate-400 font-black uppercase tracking-widest mb-3 ml-1">Note Content</label>
          <TextArea
            rows={7}
            placeholder="Type your clinical findings, observations, or follow-up instructions here..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            className="rounded-2xl border-slate-200 focus:border-teal-400 p-5 text-base transition-all shadow-sm"
          />
        </div>
      </Modal>

      {/* Modal: AI Analysis History */}
      <Modal
        title={<div className="flex items-center gap-3 py-2">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center border border-teal-500/20">
            <HiOutlineDatabase className="text-teal-600" size={20} />
          </div>
          <span className="font-black text-slate-800 tracking-tight text-xl">AI Insights History</span>
        </div>}
        open={isAiHistoryOpen}
        onCancel={() => setIsAiHistoryOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsAiHistoryOpen(false)} className="rounded-xl font-bold h-10 px-8">Dismiss</Button>
        ]}
        className="rounded-[40px] ai-history-modal"
        centered
        width={850}
      >
        <div className="py-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
          {aiHistory.length > 0 ? (
            <div className="space-y-6">
              {aiHistory.map((h, i) => (
                <div key={i} className="relative pl-10 before:absolute before:left-4 before:top-4 before:bottom-0 before:w-0.5 before:bg-slate-100 last:before:hidden">
                  <div className="absolute left-0 top-3 w-8 h-8 rounded-full bg-teal-50 border-2 border-white shadow-sm flex items-center justify-center z-10">
                    <HiOutlineSparkles className="text-teal-500" size={14} />
                  </div>
                  <div className="bg-white p-6 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] text-teal-600 font-black uppercase tracking-widest bg-teal-50 px-3 py-1 rounded-full">
                        {moment(h.created_at).format("DD MMM YYYY, hh:mm A")}
                      </span>
                      {h.appointment_id && <Tag className="rounded-lg border-slate-100 text-slate-400 font-bold px-2 py-0.5">Booking #{h.appointment_id}</Tag>}
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1.5 flex items-center gap-2">
                          <HiOutlineThumbUp size={10} /> Predicted Condition
                        </p>
                        <h5 className="font-black text-slate-800 text-base">{h.predicted_diagnosis}</h5>
                      </div>
                      <div>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1.5 flex items-center gap-2">
                          <HiOutlineDatabase size={10} /> Analyzed Symptoms
                        </p>
                        <p className="text-slate-500 text-xs font-medium italic line-clamp-1">"{h.symptoms_analyzed}"</p>
                      </div>
                    </div>
                    
                    <Divider className="my-4 border-slate-50" />
                    
                    <div className="space-y-3">
                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mb-1">Clinical Reasoning</p>
                      <p className="text-slate-600 text-xs leading-relaxed font-medium">
                        {h.clinical_reasoning}
                      </p>
                    </div>

                    {h.suggested_medicines && (
                      <div className="mt-4 pt-4 border-t border-slate-50 flex flex-wrap gap-2">
                        {(() => {
                          try {
                            const meds = typeof h.suggested_medicines === 'string' ? JSON.parse(h.suggested_medicines) : h.suggested_medicines;
                            return Array.isArray(meds) ? meds.map((m, mi) => (
                              <Tag key={mi} className="bg-slate-50 border-slate-100 text-slate-600 rounded-lg px-2 py-0.5 text-[10px] font-bold">
                                {m}
                              </Tag>
                            )) : null;
                          } catch (e) { return null; }
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-200">
              <HiOutlineDatabase size={48} className="text-slate-200 mx-auto mb-4" />
              <p className="text-slate-400 font-bold italic">No historical AI insights found for this patient.</p>
            </div>
          )}
        </div>
      </Modal>

      {/* Tooltip & Scrollbar Styling */}
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar {
            width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 20px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
        }
        .premium-table-report .ant-table-thead > tr > th {
            background: #f8fafc !important;
            font-weight: 900;
            color: #94a3b8;
            text-transform: uppercase;
            font-size: 9px;
            letter-spacing: 0.1em;
            padding: 12px 16px !important;
        }
        .premium-table-report .ant-table-row:hover td {
            background-color: #f0fdf4 !important;
        }
        .report_page_container {
            font-family: 'Outfit', sans-serif;
        }
      `}} />


      {/* --- PROFESSIONAL RX PRINT LAYOUT (Hidden from Web UI) --- */}
      <div id="rx-print-document" className="hidden print:block fixed inset-0 bg-white p-12 z-[9999]">
        {/* Rx Header: Clinic Details */}
        <div className="flex justify-between border-b-2 border-slate-900 pb-8 mb-8">
            <div className="flex-1">
                <h1 className="text-4xl font-black text-slate-900 mb-2">
                    {clinicProfile?.clinic_name || clinicProfile?.clinicName || Cookies.get("clinic_name")?.replace(/"/g, '') || "CLINIC NAME"}
                </h1>
                <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">
                    {clinicProfile?.address || clinicProfile?.clinicAddress || "Clinic Address Not Found"}
                </p>
                <p className="text-sm font-bold text-slate-500">
                    PH: {clinicProfile?.mobile_no || clinicProfile?.mobileNo || clinicProfile?.contact} | {clinicProfile?.email}
                </p>
            </div>
            <div className="text-right flex flex-col justify-center">
                <div className="text-5xl font-black text-slate-200 uppercase tracking-tighter mix-blend-multiply italic">PRESCRIPTION</div>
            </div>
        </div>

        {/* Doctor & Patient Info Header */}
        <div className="grid grid-cols-2 gap-8 mb-10 bg-slate-50 p-6 rounded-2xl border border-slate-200">
            <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-slate-400">Doctor Information</p>
                <h2 className="text-xl font-black text-slate-900">Dr. {appointment?.doctor_name || "Medical Professional"}</h2>
                <p className="text-sm font-bold text-slate-500 uppercase tracking-wide">{appointment?.speciality_name}</p>
            </div>
            <div className="space-y-4">
                <div className="flex justify-between items-start">
                    <div>
                        <p className="text-[10px] font-black uppercase text-slate-400">Patient Details</p>
                        <h2 className="text-xl font-black text-slate-900">{appointment?.name}</h2>
                        <p className="text-sm font-bold text-slate-500">{appointment?.age}Y / {appointment?.gender}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-black uppercase text-slate-400">Date</p>
                        <p className="text-sm font-black text-slate-800">{moment().format('DD/MM/YYYY')}</p>
                    </div>
                </div>
            </div>
        </div>

        {/* The Rx Symbol */}
        <div className="text-6xl font-black text-slate-900 mb-6 font-serif italic antialiased px-2">Rx</div>

        {/* Medication Table (Clean for Printing) */}
        <div className="border border-slate-200 rounded-3xl overflow-hidden mb-12">
            <table className="w-full border-collapse">
                <thead className="bg-slate-900 text-white">
                    <tr>
                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest">Medicine Name</th>
                        <th className="px-6 py-4 text-center text-[10px] font-black uppercase tracking-widest">Dose (M-A-E-N)</th>
                        <th className="px-6 py-4 text-center text-[10px] font-black uppercase tracking-widest">Duration</th>
                        <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest">Instruction</th>
                        <th className="px-6 py-4 text-center text-[10px] font-black uppercase tracking-widest">Qty</th>
                    </tr>
                </thead>
                <tbody className="text-slate-700">
                    {selectedMedications.map((med, idx) => (
                        <tr key={idx} className="border-b border-slate-100 last:border-0">
                            <td className="px-6 py-5 font-black text-slate-800 text-base">{med.name}</td>
                            <td className="px-6 py-5 text-center">
                                <div className="flex justify-center gap-2">
                                    {med.counts.map((c, i) => (
                                        <span key={i} className={`w-6 h-6 rounded-md flex items-center justify-center font-bold text-xs ${c > 0 ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-300'}`}>
                                            {c}
                                        </span>
                                    ))}
                                </div>
                            </td>
                            <td className="px-6 py-5 text-center font-bold">{med.days} Days</td>
                            <td className="px-6 py-5 font-bold italic">{med.foodtype === 0 ? "Before Food" : "After Food"}</td>
                            <td className="px-6 py-5 text-center font-black text-slate-900">{med.totalmedicine}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        {/* Diagnosis & Note Summary */}
        <div className="mb-12 px-6">
            <h4 className="text-[10px] font-black uppercase text-slate-400 mb-2">Final Diagnosis / Clinical Remark</h4>
            <p className="text-sm font-bold text-slate-700 leading-relaxed border-l-4 border-slate-900 pl-4">
                This prescription is prepared based on the clinical evaluation performed on {moment().format('MMMM DD, YYYY')}.
            </p>
        </div>

        {/* Footer Signature Area */}
        <div className="mt-24 flex justify-between items-end px-6">
            <div className="text-[10px] font-bold text-slate-400 uppercase">System Digital ID: {selectedPreview?.id}</div>
            <div className="text-center w-64 border-t-2 border-slate-900 pt-4">
                <p className="text-xs font-black text-slate-900 uppercase tracking-widest">Physician's Signature & Seal</p>
                <p className="text-[9px] font-bold text-slate-400 mt-1">Dr. {appointment?.doctor_name}</p>
            </div>
        </div>

        {/* CSS for print injection */}
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            @page {
              size: auto;
              margin: 10mm;
            }
            body * { visibility: hidden !important; }
            #rx-print-document, #rx-print-document * { visibility: visible !important; }
            #rx-print-document { 
               position: absolute !important; 
               left: 0 !important; 
               top: 0 !important; 
               width: 100% !important; 
               height: auto !important;
               min-height: 0 !important;
               background: white !important; 
               z-index: 100000 !important; 
               padding: 5mm !important;
               margin: 0 !important;
            }
            html, body {
              height: auto !important;
              overflow: visible !important;
            }
          }
        ` }} />
      </div>
    </div>
  );
};

export default Report;
